/**
 * La disputa de un cobro, con la interfaz de Stripe, dentro de nuestro panel.
 *
 * Es el componente incrustado `payment-disputes`: el formulario de pruebas, el
 * motivo que alegó el comprador, el plazo y el botón de responder o aceptar la
 * pérdida — servido por Stripe y firmado para la cuenta conectada.
 *
 * Existe aparte de `StripePaymentDetails` aunque aquélla también sepa disputar.
 * Allí la disputa es una sección más bajo la cronología del pago, y quien llega
 * aquí desde el aviso de arriba viene a hacer una sola cosa antes de una fecha.
 * Abrir la pantalla que contiene esa cosa, en vez de la cosa, es pedirle que la
 * busque — y el motivo por el que el aviso existe es que el tiempo corre.
 *
 * Lo que no se delega, igual que en el resto: qué oferta se vendió y quién la
 * compró. Eso lo dice nuestra ficha, que es desde donde se abre esto.
 */
import { useI18n } from '../../../lib/i18n'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ConnectPaymentDisputes } from '@stripe/react-connect-js'
import { EmbedLoading, StripeEmbedProvider, embedErrorMessage } from './StripeEmbed'
import { IconX } from '@tabler/icons-react'

export function StripeDisputeDetails({ projectId, token, paymentId, onClose }: {
  projectId: string
  token: string
  /** El PaymentIntent del cobro disputado, tal como lo conoce la cuenta. */
  paymentId: string
  onClose: () => void
}) {
  const { t } = useI18n()
  const [failed, setFailed] = useState('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div
      className="dash-modal-backdrop dash-stripe-embed-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <div className="dash-stripe-embed dash-stripe-dispute-embed" role="dialog" aria-modal="true" aria-label={t('stripe.embed.disputeAria')}>
        {/* Aspa propia, al revés que en `payment-details`: aquel componente trae
            la suya y su cabecera, éste no. Sin esto la única salida sería la
            tecla de escape o pulsar fuera, que no se ven. */}
        <button type="button" className="dash-stripe-embed-close" aria-label={t('stripe.embed.close')} onClick={onClose}>
          <IconX size={16} />
        </button>

        {failed ? (
          <p className="dash-order-detail-note">{failed}</p>
        ) : (
          <StripeEmbedProvider
            projectId={projectId}
            token={token}
            fallback={<EmbedLoading label={t('stripe.embed.openingDispute')} />}
            onFail={setFailed}
          >
            <ConnectPaymentDisputes
              payment={paymentId}
              onLoadError={(event) => {
                console.error('[ventas] Stripe no cargó la disputa', event.error)
                setFailed(embedErrorMessage(event.error, 'stripe.embed.subject.dispute', t))
              }}
            />
          </StripeEmbedProvider>
        )}
      </div>
    </div>,
    document.body,
  )
}
